// Note: there are a number of lines in this file where the ESLint rule
// react/no-access-state-in-setstate has been disabled. In these cases, some of
// the intermediate values used in the state update are passed to
// updateSearchState within the setState callback which makes these difficult to
// refactor. We are ignoring these for now, because all of these issues can be
// fixed more easily when we refactor this functionality to use React hooks.
import React from 'react';
import qs from 'query-string';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import removeFalsyValuesInObj from 'commons/removeFalsyValuesInObj';
import { FACET_FILTER_VALUES, EXCLUDED_FILTER_PILLS } from './utils';

export const withSearchState = Component => {
  class ComponentWithSearchState extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        mainIndex: 'customer_photos',
        isMobileFiltersOpen: false,
        currentProductPage: 1,
        searchState: {
          configure: {
            facetFilters: '',
            facets: ['*'],
          },
          hitsPerPage: 24,
          page: 1,
          query: '',
        },
        roomFilter: '',
        colorFilters: {},
        materialFilter: '',
        materialOption: '',
        isProductIdFiltered: {
          isFiltered: false,
          currentProductId: null,
          productName: '',
        },
        isFiltered: false,
        selectedFiltersList: [],
      };
    }

    componentDidMount() {
      this.setStateFromQueryParams();
    }

    setStateFromQueryParams = () => {
      const params = qs.parse(window.location.search);
      if (window.location.pathname.includes('customer-photos')) {
        const updatedSelectedFiltersList = [];
        if (params.room) {
          this.onRoomFilterToggle(params.room);
        }

        if (params.color) {
          const multipleColors = params.color.indexOf(',') > -1;
          if (multipleColors) {
            this.onColorFiltersToggle(params.color.split(','));
          } else {
            this.onColorFilterToggle(params.color);
          }
        }

        if (params.material) {
          this.onMaterialFilterToggle(params.material);
        }

        if (params.pid && params.pname) {
          this.onProductFilterChange(params.pid, params.pname, false);

          updatedSelectedFiltersList.push({
            filterName: params.pname,
            onDeselect: () =>
              this.onProductFilterChange(params.pid, params.pname),
            name: 'product',
          });
        }

        if (params.photoid) {
          this.onImageChange(params.photoid);
        }

        if (params.materialOption) {
          this.onMaterialOptionFilterToggle(params.materialOption, false);
          updatedSelectedFiltersList.push({
            filterName: params.materialOption,
            onDeselect: this.onMaterialOptionFilterToggle,
            name: 'materialOption',
          });
        }

        if (!isEmpty(updatedSelectedFiltersList)) {
          this.setState({
            selectedFiltersList: updatedSelectedFiltersList,
          });
        }
      }
    };

    onSearchStateChange = searchState =>
      this.setState({
        searchState,
      });

    onLoadMoreResults = () => {
      // eslint-disable-next-line react/no-access-state-in-setstate
      const updatedSearch = { ...this.state.searchState };
      updatedSearch.page = this.state.currentProductPage + 1;
      this.setState(
        {
          currentProductPage: updatedSearch.page,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    resetAllFilters = async () => {
      this.setState({
        roomFilter: '',
        materialFilter: '',
        colorFilters: {},
        selectedFiltersList: [],
      });
    };

    updateSearchState = search => {
      let isFiltered = false;
      const facetFilters = [];
      const updatedSearch = { ...search };

      const {
        roomFilter,
        colorFilters,
        materialFilter,
        isProductIdFiltered,
        materialOption,
        photoIdFilter,
      } = this.state;

      if (roomFilter) {
        isFiltered = true;
        facetFilters.push(`"${FACET_FILTER_VALUES.room}${roomFilter}"`);
      }

      if (materialFilter) {
        isFiltered = true;
        facetFilters.push(`"${FACET_FILTER_VALUES.material}${materialFilter}"`);
      }
      // If Option Value exists add that to search
      if (materialOption) {
        facetFilters.push(
          `"${FACET_FILTER_VALUES.option_value}${materialOption}"`
        );
      }

      if (isProductIdFiltered.currentProductId) {
        isFiltered = true;
        facetFilters.push(
          `"${FACET_FILTER_VALUES.product_id}${isProductIdFiltered.currentProductId}"`
        );
      }

      const colorsKeys = Object.keys(colorFilters);
      if (colorsKeys.length) {
        isFiltered = true;
        facetFilters.push(
          `[${colorsKeys
            .map(color => `"${FACET_FILTER_VALUES.color}${color}"`)
            .join(',')}]`
        );
      }

      updatedSearch.configure.facetFilters = facetFilters.length
        ? `[${facetFilters.join(',')}]`
        : '';
      this.setState({
        isFiltered,
        searchState: updatedSearch,
      });

      const searchParamsObj = {
        ...(this.props.history?.location?.query ??
          this.props.history?.location?.instantQuery ??
          qs.parse(this.props.history.location.search)),
        pid: isProductIdFiltered?.currentProductId,
        pname: isProductIdFiltered?.productName,
        materialOption,
        material: materialFilter,
        room: roomFilter,
        color: Object.keys(colorFilters).join(',') || '',
        photoid: photoIdFilter,
      };

      this.props.history.push({
        pathname: this.props.history.location.pathname,
        search: qs.stringify(removeFalsyValuesInObj(searchParamsObj), {
          encode: true,
        }),
        instantQuery: searchParamsObj
      });
    };

    toggleFromSelectedFilters = (filterName, filterToggle, name) => {
      this.setState(prevState => {
        const updatedSelectedFilters = [...prevState.selectedFiltersList];

        if (EXCLUDED_FILTER_PILLS.includes(name)) {
          return prevState;
        }

        // Check if filterName in selected filters
        const filterNameIndex = updatedSelectedFilters.findIndex(
          filter => filter.filterName === filterName
        );

        if (filterNameIndex > -1) {
          updatedSelectedFilters.splice(filterNameIndex, 1);
        } else {
          const filterIndex = updatedSelectedFilters.findIndex(
            filter => filter.name === name
          );

          if (filterIndex > -1) {
            updatedSelectedFilters.splice(filterIndex, 1);
          }

          if (filterName) {
            updatedSelectedFilters.push({
              filterName,
              onDeselect: filterToggle,
              name,
            });
          }
        }

        return {
          selectedFiltersList: updatedSelectedFilters,
        };
      });
    };

    openMobileFilters = () => {
      this.setState({ isMobileFiltersOpen: true });
    };

    closeMobileFilters = () => {
      this.setState({ isMobileFiltersOpen: false });
    };

    onClearFilters = async () => {
      const updatedSearch = { ...this.state.searchState };
      updatedSearch.page = 1;
      await this.resetAllFilters();
      this.updateSearchState(updatedSearch);
    };

    onRoomFilterToggle = room => {
      const updatedSearch = { ...this.state.searchState, page: 1 };
      // eslint-disable-next-line react/no-access-state-in-setstate
      const updatedRoomFilter = this.state.roomFilter !== room ? room : '';
      this.toggleFromSelectedFilters(room, this.onRoomFilterToggle, 'room');
      this.setState(
        {
          roomFilter: updatedRoomFilter,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    onMaterialFilterToggle = material => {
      const updatedSearch = { ...this.state.searchState, page: 1 };
      const updatedMaterialFilter =
        // eslint-disable-next-line react/no-access-state-in-setstate
        this.state.materialFilter !== material ? material : '';

      this.setState(
        {
          materialFilter: updatedMaterialFilter,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    onMaterialOptionFilterToggle = (
      materialOption,
      updateSelectedFilters = true
    ) => {
      const updatedSearch = { ...this.state.searchState, page: 1 };
      const updatedMaterialOption =
        // eslint-disable-next-line react/no-access-state-in-setstate
        this.state.materialOption !== materialOption ? materialOption : '';

      if (updateSelectedFilters) {
        this.toggleFromSelectedFilters(
          materialOption,
          this.onMaterialOptionFilterToggle,
          'materialOption'
        );
      }
      this.setState(
        {
          materialOption: updatedMaterialOption,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    onColorFiltersToggle = async colors => {
      const updatedSearch = { ...this.state.searchState, page: 1 };
      // eslint-disable-next-line react/no-access-state-in-setstate
      const updatedColorFilters = { ...this.state.colorFilters };
      colors.forEach(color => {
        // Check if color is already filtered
        if (this.state.colorFilters[color]) {
          // Remove Color
          delete updatedColorFilters[color];
        } else {
          // Add filter
          updatedColorFilters[color] = true;
        }
        this.toggleFromSelectedFilters(
          color,
          this.onColorFilterToggle,
          'color'
        );
      });

      this.setState(
        {
          colorFilters: updatedColorFilters,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    onColorFilterToggle = async color => {
      const updatedSearch = { ...this.state.searchState, page: 1 };
      // eslint-disable-next-line react/no-access-state-in-setstate
      const updatedColorFilters = { ...this.state.colorFilters };

      // Check if color is already filtered
      if (this.state.colorFilters[color]) {
        // Remove Color
        delete updatedColorFilters[color];
      } else {
        // Add filter
        updatedColorFilters[color] = true;
      }

      this.toggleFromSelectedFilters(color, this.onColorFilterToggle, 'color');
      this.setState(
        {
          colorFilters: updatedColorFilters,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    onProductFilterChange = (
      productId,
      productName,
      updateSelectedFilters = true
    ) => {
      const updatedSearch = { ...this.state.searchState, page: 1 };
      // eslint-disable-next-line react/no-access-state-in-setstate
      const updatedProductIdFilter = { ...this.state.isProductIdFiltered };
      if (
        productId === '' ||
        updatedProductIdFilter.currentProductId === productId
      ) {
        updatedProductIdFilter.isFiltered = false;
        updatedProductIdFilter.currentProductId = null;
        updatedProductIdFilter.productName = '';
      } else {
        updatedProductIdFilter.isFiltered = true;
        updatedProductIdFilter.currentProductId = productId;
        updatedProductIdFilter.productName = productName;
      }

      if (updateSelectedFilters) {
        this.toggleFromSelectedFilters(
          productName,
          () => this.onProductFilterChange(productId, productName),
          'product'
        );
      }

      this.setState(
        {
          isProductIdFiltered: updatedProductIdFilter,
        },
        () => {
          this.updateSearchState(updatedSearch);
        }
      );
    };

    onImageChange = photoId => {
      this.setState(
        prevState => {
          const updatedProductIdFilter =
            prevState.photoIdFilter !== photoId ? photoId : '';

          return {
            photoIdFilter: updatedProductIdFilter,
          };
        },
        () => {
          this.updateSearchState({ ...this.state.searchState, page: 1 });
        }
      );
    };

    render() {
      const actions = {
        onSearchStateChange: this.onSearchStateChange,
        onLoadMoreResults: this.onLoadMoreResults,
      };

      const filterActions = {
        onRoomFilterChange: this.onRoomFilterToggle,
        onMaterialFilterChange: this.onMaterialFilterToggle,
        onMaterialOptionChange: this.onMaterialOptionFilterToggle,
        onColorFilterChange: this.onColorFilterToggle,
        onClearFilters: this.onClearFilters,
        onProductIdFilterChange: this.onProductFilterChange,
        openMobileFilters: this.openMobileFilters,
        closeMobileFilters: this.closeMobileFilters,
        onImageSelect: this.onImageChange,
      };

      const filterState = {
        roomFilter: this.state.roomFilter,
        colorFilters: this.state.colorFilters,
        materialFilter: this.state.materialFilter,
        isProductIdFiltered: this.state.isProductIdFiltered,
        isMaterialOptionFiltered: this.state.materialOption,
        isFiltered: this.state.isFiltered,
        selectedFiltersList: this.state.selectedFiltersList,
        isMobileFiltersOpen: this.state.isMobileFiltersOpen,
        photoId: this.state.photoIdFilter,
      };

      const state = {
        searchState: this.state.searchState,
        mainIndex: this.state.mainIndex,
      };

      return (
        <Component
          state={state}
          actions={actions}
          filterActions={filterActions}
          filterState={filterState}
          {...this.props}
        />
      );
    }
  }

  ComponentWithSearchState.propTypes = {
    history: PropTypes.shape({
      location: PropTypes.shape({
        pathname: PropTypes.string,
        search: PropTypes.string,
        query: PropTypes.object,
        instantQuery: PropTypes.object,
      }),
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  return ComponentWithSearchState;
};
